import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const Rinoplastia = () => {
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Cirugía Facial',
      title: 'Rinoplastia',
      description: 'Corrección estética y/o funcional de la nariz. Planificación con morphing digital previo para visualizar el resultado esperado. Abordaje abierto o cerrado según las necesidades de cada caso.',
      requestAssessment: 'Solicitar Valoración',
      callNow: 'Llamar Ahora',
      benefitsTitle: 'Beneficios',
      resultsTitle: 'Resultados de la cirugía',
      benefits: [
        'Corrección estética y funcional de la nariz',
        'Planificación con morphing digital previo',
        'Abordaje abierto o cerrado según el caso',
        'Resultados naturales y armónicos',
        'Mejora de la respiración si hay desviación septal',
        'Recuperación progresiva en 2-3 semanas',
      ],
      proceduresTitle: 'Tipos de Rinoplastia',
      procedures: [
        { titulo: 'Rinoplastia Estética', descripcion: 'Corrección de la forma, tamaño y proporciones de la nariz para lograr armonía facial.' },
        { titulo: 'Rinoplastia Funcional', descripcion: 'Mejora de la respiración mediante corrección del tabique nasal o cornetes.' },
        { titulo: 'Rinoplastia Secundaria', descripcion: 'Revisión de cirugías nasales previas con resultados insatisfactorios.' },
        { titulo: 'Punta Nasal', descripcion: 'Refinamiento específico de la punta de la nariz sin modificar el dorso.' },
      ],
      ctaTitle: '¿Desea más información?',
      ctaDesc: 'Solicite una consulta de valoración personalizada.',
      contact: 'Contactar',
    },
    en: {
      eyebrow: 'Facial Surgery',
      title: 'Rhinoplasty',
      description: 'Aesthetic and/or functional nose correction. Planning with prior digital morphing to visualize the expected result. Open or closed approach depending on each case.',
      requestAssessment: 'Request Assessment',
      callNow: 'Call Now',
      benefitsTitle: 'Benefits',
      resultsTitle: 'Surgery results',
      benefits: [
        'Aesthetic and functional nose correction',
        'Planning with prior digital morphing',
        'Open or closed approach as needed',
        'Natural and harmonious results',
        'Improved breathing if septal deviation',
        'Progressive recovery in 2-3 weeks',
      ],
      proceduresTitle: 'Types of Rhinoplasty',
      procedures: [
        { titulo: 'Aesthetic Rhinoplasty', descripcion: 'Correction of nose shape, size and proportions to achieve facial harmony.' },
        { titulo: 'Functional Rhinoplasty', descripcion: 'Breathing improvement through correction of nasal septum or turbinates.' },
        { titulo: 'Secondary Rhinoplasty', descripcion: 'Revision of previous nasal surgeries with unsatisfactory results.' },
        { titulo: 'Nasal Tip', descripcion: 'Specific refinement of the nasal tip without modifying the bridge.' },
      ],
      ctaTitle: 'Want more information?',
      ctaDesc: 'Request a personalized assessment consultation.',
      contact: 'Contact',
    },
    fr: {
      eyebrow: 'Chirurgie Faciale',
      title: 'Rhinoplastie',
      description: 'Correction esthétique et/ou fonctionnelle du nez. Planification avec morphing digital préalable pour visualiser le résultat attendu. Approche ouverte ou fermée selon chaque cas.',
      requestAssessment: 'Demander une Évaluation',
      callNow: 'Appeler Maintenant',
      benefitsTitle: 'Avantages',
      resultsTitle: 'Résultats de la chirurgie',
      benefits: [
        'Correction esthétique et fonctionnelle du nez',
        'Planification avec morphing digital préalable',
        'Approche ouverte ou fermée selon le cas',
        'Résultats naturels et harmonieux',
        'Amélioration de la respiration si déviation septale',
        'Récupération progressive en 2-3 semaines',
      ],
      proceduresTitle: 'Types de Rhinoplastie',
      procedures: [
        { titulo: 'Rhinoplastie Esthétique', descripcion: 'Correction de la forme, taille et proportions du nez pour atteindre l\'harmonie faciale.' },
        { titulo: 'Rhinoplastie Fonctionnelle', descripcion: 'Amélioration de la respiration par correction de la cloison nasale ou cornets.' },
        { titulo: 'Rhinoplastie Secondaire', descripcion: 'Révision de chirurgies nasales précédentes avec résultats insatisfaisants.' },
        { titulo: 'Pointe Nasale', descripcion: 'Affinement spécifique de la pointe du nez sans modifier le dos.' },
      ],
      ctaTitle: 'Vous voulez plus d\'informations?',
      ctaDesc: 'Demandez une consultation d\'évaluation personnalisée.',
      contact: 'Contacter',
    },
    pt: {
      eyebrow: 'Cirurgia Facial',
      title: 'Rinoplastia',
      description: 'Correção estética e/ou funcional do nariz. Planejamento com morphing digital prévio para visualizar o resultado esperado. Abordagem aberta ou fechada segundo cada caso.',
      requestAssessment: 'Solicitar Avaliação',
      callNow: 'Ligar Agora',
      benefitsTitle: 'Benefícios',
      resultsTitle: 'Resultados da cirurgia',
      benefits: [
        'Correção estética e funcional do nariz',
        'Planejamento com morphing digital prévio',
        'Abordagem aberta ou fechada conforme o caso',
        'Resultados naturais e harmônicos',
        'Melhora da respiração se houver desvio de septo',
        'Recuperação progressiva em 2-3 semanas',
      ],
      proceduresTitle: 'Tipos de Rinoplastia',
      procedures: [
        { titulo: 'Rinoplastia Estética', descripcion: 'Correção da forma, tamanho e proporções do nariz para alcançar harmonia facial.' },
        { titulo: 'Rinoplastia Funcional', descripcion: 'Melhora da respiração mediante correção do septo nasal ou cornetos.' },
        { titulo: 'Rinoplastia Secundária', descripcion: 'Revisão de cirurgias nasais prévias com resultados insatisfatórios.' },
        { titulo: 'Ponta Nasal', descripcion: 'Refinamento específico da ponta do nariz sem modificar o dorso.' },
      ],
      ctaTitle: 'Deseja mais informações?',
      ctaDesc: 'Solicite uma consulta de avaliação personalizada.',
      contact: 'Contatar',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.eyebrow}</p>
              <h1 className="font-serif text-5xl md:text-6xl text-[#1C1C1A] mb-6 leading-tight">{t.title}</h1>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-lg text-[#4A4845] leading-relaxed mb-8">{t.description}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
                  {t.requestAssessment}
                  <ArrowRight size={16} />
                </Link>
                <a href="tel:+34952764547" className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors">
                  <Phone size={16} />
                  {t.callNow}
                </a>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft" className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#B8975A]"></div>
              <img src="https://images.pexels.com/photos/5069612/pexels-photo-5069612.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Rinoplastia" className="w-full h-auto" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#B8975A]"></div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-6xl">
          <AnimatedSection animation="fadeUp">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.benefitsTitle}</p>
            <h2 className="font-serif text-4xl text-white mb-8">{t.resultsTitle}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {t.benefits.map((beneficio, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#B8975A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={14} className="text-[#B8975A]" />
                  </div>
                  <span className="text-white/80">{beneficio}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Procedimientos */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-6xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <h2 className="font-serif text-4xl text-[#1C1C1A]">{t.proceduresTitle}</h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-8">
            {t.procedures.map((proc, index) => (
              <AnimatedSection key={index} animation="fadeUp" delay={index * 0.1}>
                <div className="bg-white p-8 border-b-2 border-[#B8975A] h-full">
                  <span className="text-[#B8975A]/40 font-serif text-sm">0{index + 1}</span>
                  <h3 className="font-serif text-2xl text-[#1C1C1A] mt-2 mb-4">{proc.titulo}</h3>
                  <p className="text-[#4A4845]">{proc.descripcion}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <h2 className="font-serif text-4xl text-[#1C1C1A] mb-6">{t.ctaTitle}</h2>
            <p className="text-[#4A4845] mb-10">{t.ctaDesc}</p>
            <Link to="/contacto" className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
              {t.contact}
              <ArrowRight size={16} />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Rinoplastia;
